import React, { useEffect, useState } from "react";
import { RenderMedicaidDocumentsModalProps } from "./types";
import { useSelector } from "redux/reducer";
import { shallowEqual, useDispatch } from "react-redux";
import {
  getMedicaidLeadDocuments,
  renderMedicaidDocuments,
} from "modules/medicaid/actions";
import { RenderedDocumentStatus } from "modules/api/types/WorkflowDetailsPayload";
import { API, getBlob } from "modules/api";

import styles from "./styles.module.scss";
import classNames from "classnames/bind";
import AnimatedLoadingOverlay from "components/AnimatedLoadingOverlay";
import { AnimatedLoadingOverlaySize } from "components/AnimatedLoadingOverlay/types";

const cx = classNames.bind(styles);

const RENDER_INTERVAL = 2000;

const isUsableUrl = (url: unknown): url is string =>
  typeof url === "string" && url.length > 0 && !url.startsWith("file://");

const RenderMedicaidDocumentsModal = ({
  medicaidLeadId,
}: RenderMedicaidDocumentsModalProps) => {
  const dispatch = useDispatch();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { documents, isRenderingMedicaidLeadDocuments } = useSelector(
    (state) => ({
      documents: state.medicaid.admin?.documents,
      isRenderingMedicaidLeadDocuments:
        state.medicaid.isRenderingMedicaidLeadDocuments,
    }),
    shallowEqual,
  );

  useEffect(() => {
    let fetchInterval: NodeJS.Timeout | undefined;

    // Only trigger initial render when we have no document state yet (first open).
    // Do not re-trigger when documents === [] (error state), to avoid a retry loop.
    if (documents === undefined && !isRenderingMedicaidLeadDocuments) {
      renderMedicaidDocuments(dispatch, medicaidLeadId);
      return;
    }

    // Poll for status updates only when we have documents for this lead and some are still pending
    const medicaidLeadDocuments =
      documents?.filter((doc) => doc.medicaidLeadId === medicaidLeadId) || [];
    const hasPendingForLead = medicaidLeadDocuments.some(
      (doc) => doc.status === RenderedDocumentStatus.PENDING,
    );

    if (hasPendingForLead) {
      fetchInterval = setInterval(() => {
        if (!isRenderingMedicaidLeadDocuments) {
          getMedicaidLeadDocuments(dispatch, medicaidLeadId);
        }
      }, RENDER_INTERVAL);
    }

    return () => {
      if (fetchInterval !== undefined) clearInterval(fetchInterval);
    };
  }, [dispatch, documents, isRenderingMedicaidLeadDocuments, medicaidLeadId]);

  if (documents === undefined) {
    return (
      <div>
        <AnimatedLoadingOverlay size={AnimatedLoadingOverlaySize.MEDIUM} />
        {isRenderingMedicaidLeadDocuments && (
          <p className={cx("rendered-medicaid-lead-documents__hint")}>
            Rendering can take a minute or two…
          </p>
        )}
      </div>
    );
  }

  if (documents.length === 0 && !isRenderingMedicaidLeadDocuments) {
    return (
      <p className={cx("rendered-medicaid-lead-documents__error")}>
        No documents to display. If you just tried to render, there may have
        been an error—please try again or cancel.
      </p>
    );
  }

  const handleDownload = async (documentDefinitionId: string) => {
    setDownloadingId(documentDefinitionId);
    try {
      const endpoint = API.GET_MEDICAID_LEAD_DOCUMENT_DOWNLOAD({
        medicaidLeadId,
        documentDefinitionId,
      });
      const blob = await getBlob(endpoint);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      const status = err?.response?.status;
      const message =
        status === 403
          ? "Your session may have expired. Please try again."
          : status === 404
            ? "Document not found on server."
            : "Failed to open document.";
      window.alert(message);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div>
      <ol className={cx("rendered-medicaid-lead-documents")}>
        {documents.map((renderedDocument) => (
          <li
            className={cx("rendered-medicaid-lead-document")}
            key={`${renderedDocument.medicaidLeadId}-${renderedDocument.documentDefinitionId}`}
          >
            <div className={cx("rendered-medicaid-lead-document__title")}>
              {renderedDocument.displayName}:
            </div>
            {renderedDocument.status === RenderedDocumentStatus.FINISHED ? (
              isUsableUrl(renderedDocument.signedUrl) ? (
                <a
                  href={renderedDocument.signedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Finished
                </a>
              ) : (
                <button
                  type="button"
                  className={cx("rendered-medicaid-lead-document__link")}
                  disabled={
                    downloadingId === renderedDocument.documentDefinitionId
                  }
                  onClick={() =>
                    handleDownload(renderedDocument.documentDefinitionId)
                  }
                >
                  {downloadingId === renderedDocument.documentDefinitionId
                    ? "Opening…"
                    : "Finished"}
                </button>
              )
            ) : (
              <div>{renderedDocument.status}</div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

export default RenderMedicaidDocumentsModal;
